import React, { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Check, Crown, Zap, Users, ArrowLeft, Lock, Eye, EyeOff, UserPlus, Shield, Star, Sparkles, Award, Clock, CheckCircle } from 'lucide-react';

export default function RegisterPage() {
  const renderPlanFeature = (feature: string) => {
    if (feature.includes("$8 after")) {
      const [prefix] = feature.split("· $8 after");
      return (
        <>
          {prefix.trim()} · <span className="line-through text-slate-500">$8 after</span> ·{" "}
          <span className="text-green-700 font-semibold">now completely free</span>
        </>
      );
    }
    return feature;
  };

  const [selectedPlan, setSelectedPlan] = useState('basic');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    username: '',
    companyName: '',
    phone: '',
  });
  
  const [, setLocation] = useLocation();
  const [isProcessing, setIsProcessing] = useState(false);

  // Preselect plan from query string (?plan=pro|basic|business)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const plan = (params.get('plan') || '').toLowerCase();
      if (['basic', 'pro', 'business'].includes(plan)) {
        setSelectedPlan(plan);
      }
    } catch {}
  }, []);

  const plans = [
    {
      id: 'basic',
      name: 'Basic',
      price: 49,
      icon: Users,
      features: ['1 user · +$11/user for team', 'Clients, estimates & invoices', 'Project tracking & client portal', 'Email support'],
      popular: false,
      color: 'green'
    },
    {
      id: 'pro',
      name: 'Pro',
      price: 79,
      icon: Zap,
      features: ['1 user · +$11/user for team', '5 AI roof reports/month · $8 after', 'AI estimates & project tasks', 'Calendar, time clock', 'Priority support'],
      popular: true,
      color: 'green'
    },
    {
      id: 'business',
      name: 'Business',
      price: 149,
      icon: Crown,
      features: [
        '1 user · +$11/user for team',
        '5 AI roof reports/month · $8 after',
        'Advanced client portal experience',
        'Team/agents management & full timeclock',
        'Premium support'
      ],
      popular: false,
      color: 'green'
    }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Double-check chosen plan with the user
    const chosenPlan = plans.find((p) => p.id === selectedPlan);
    const chosenPlanName = chosenPlan?.name || selectedPlan;
    const confirmed = window.confirm(
      `You are about to create an account on the "${chosenPlanName}" plan. Is this the plan you want?`
    );
    if (!confirmed) {
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      alert('Passwords do not match');
      return;
    }
    
    // Direct account creation - no card required at signup
    setIsProcessing(true);
    
    try {
      // Registration API starts app-level free trial
      const response = await fetch('/api/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          username: formData.username,
          companyName: formData.companyName,
          plan: selectedPlan
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        sessionStorage.removeItem('pendingRegistration');
        console.log('✅ Account created, redirecting to verification:', data.email);
        window.location.href = `/verify-email?email=${encodeURIComponent(data.email || formData.email)}`;
      } else {
        // Show error message
        alert(data.error || 'Registration failed. Please try again.');
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('❌ Registration error:', error);
      alert('An error occurred during registration. Please try again.');
      setIsProcessing(false);
    }
  };

  return (
    <div className="light min-h-screen bg-white text-slate-900">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto space-y-8">
          {/* Header Card */}
          <Card className="!bg-white !border-slate-300">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-slate-800">
                    <UserPlus className="h-6 w-6 text-green-500" />
                    Create Your Account
                  </CardTitle>
                  <p className="text-slate-600 mt-2">
                    Join thousands of contractors who trust Remodra to manage their business
                  </p>
                </div>
                <Button
                  variant="ghost"
                  onClick={() => setLocation('/')}
                  className="text-slate-600 hover:text-green-500"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Home
                </Button>
              </div>
            </CardHeader>
          </Card>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Registration Form */}
            <Card className="!bg-white !border-slate-300">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-slate-800">
                  <UserPlus className="h-5 w-5 text-green-500" />
                  Account Information
                </CardTitle>
                <p className="text-slate-600">
                  Fill in your details to get started
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Personal Information */}
                  <div className="space-y-4">
                    <h4 className="font-semibold text-slate-800">Personal Information</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          First Name *
                        </label>
                        <Input
                          type="text"
                          value={formData.firstName}
                          onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                          placeholder="Enter your first name"
                          className="w-full"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Last Name *
                        </label>
                        <Input
                          type="text"
                          value={formData.lastName}
                          onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                          placeholder="Enter your last name"
                          className="w-full"
                          required
                        />
                      </div>
                    </div>
                    
                    {/* Username Field */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Username *
                      </label>
                      <Input
                        type="text"
                        value={formData.username}
                        onChange={(e) => setFormData({...formData, username: e.target.value})}
                        placeholder="Choose a unique username"
                        className="w-full"
                        required
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        This will be your unique identifier in the system
                      </p>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="space-y-4">
                    <h4 className="font-semibold text-slate-800">Contact Information</h4>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Email Address *
                      </label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder="Enter your email address"
                        className="w-full"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Phone Number
                      </label>
                      <Input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        placeholder="Enter your phone number"
                        className="w-full"
                      />
                    </div>
                  </div>

                  {/* Company Information */}
                  <div className="space-y-4">
                    <h4 className="font-semibold text-slate-800">Company Information</h4>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Company Name *
                      </label>
                      <Input
                        type="text"
                        value={formData.companyName}
                        onChange={(e) => setFormData({...formData, companyName: e.target.value})}
                        placeholder="Enter your company name"
                        className="w-full"
                        required
                      />
                    </div>
                  </div>

                  {/* Password */}
                  <div className="space-y-4">
                    <h4 className="font-semibold text-slate-800">Security</h4>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Password *
                      </label>
                      <div className="relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          value={formData.password}
                          onChange={(e) => setFormData({...formData, password: e.target.value})}
                          placeholder="Create a strong password"
                          className="w-full pr-10"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Confirm Password *
                      </label>
                      <div className="relative">
                        <Input
                          type={showConfirmPassword ? "text" : "password"}
                          value={formData.confirmPassword}
                          onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                          placeholder="Confirm your password"
                          className="w-full pr-10"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    className="w-full bg-green-600 hover:bg-green-700 text-white"
                    disabled={isProcessing}
                  >
                    {isProcessing ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Creating Account...
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <UserPlus className="h-4 w-4 mr-2" />
                        Create Account
                      </div>
                    )}
                  </Button>

                </form>
              </CardContent>
            </Card>

            {/* Plan Selection */}
            <div className="space-y-6">
              <Card className="!bg-white !border-slate-300">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-slate-800">
                    <Crown className="h-5 w-5 text-green-500" />
                    Choose Your Plan
                  </CardTitle>
                  <p className="text-slate-600">
                    Start instantly with no card required — your free trial begins right away
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  {plans.map((plan) => {
                    const IconComponent = plan.icon;
                    return (
                      <div
                        key={plan.id}
                        className={`p-4 border rounded-lg cursor-pointer transition-all ${
                          selectedPlan === plan.id
                            ? 'border-2 border-green-600 bg-green-50 shadow-lg scale-[1.01]'
                            : 'border-slate-300 hover:border-green-300'
                        }`}
                        onClick={() => setSelectedPlan(plan.id)}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-green-100">
                              <IconComponent className="h-5 w-5 text-green-600" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-slate-800">{plan.name}</h3>
                              <p className="text-2xl font-bold text-green-600">${plan.price}/month</p>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            {plan.popular && (
                              <Badge className="bg-green-100 text-green-800">
                                Most Popular · Win More Bids
                              </Badge>
                            )}
                            {selectedPlan === plan.id && (
                              <Badge className="bg-green-600 text-white">
                                Selected
                              </Badge>
                            )}
                          </div>
                        </div>
                        <ul className="space-y-1 text-sm text-slate-600">
                          {plan.features.map((feature, index) => (
                            <li key={index} className="flex items-center gap-2">
                              <Check className="h-3 w-3 text-green-500 flex-shrink-0" />
                              {renderPlanFeature(feature)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>

              {/* Trust Indicators */}
              <Card className="bg-green-50 border border-green-200">
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    <h4 className="font-semibold text-green-800">What's Included</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="flex items-center gap-2 text-green-700">
                        <Clock className="h-4 w-4" />
                        <span>Free trial starts now (no card)</span>
                      </div>
                      <div className="flex items-center gap-2 text-green-700">
                        <Shield className="h-4 w-4" />
                        <span>Enterprise Security</span>
                      </div>
                      <div className="flex items-center gap-2 text-green-700">
                        <Star className="h-4 w-4" />
                        <span>4.9/5 Rating</span>
                      </div>
                      <div className="flex items-center gap-2 text-green-700">
                        <CheckCircle className="h-4 w-4" />
                        <span>Cancel Anytime</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
